import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import Teachers from './pages/Teachers';
import Monitor from './pages/Monitor';
import Security from './pages/Security';
import Perf from './pages/Perf';
import Bandwidth from './pages/Bandwidth';
import { ProLayout } from '@ant-design/pro-components';
import { Avatar, Button, Drawer, Dropdown, Grid, Layout as AntLayout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import logoPet from './assets/logo-pet.svg';
import api from './services/api';
import { useState } from 'react';

const MENU_DATA = [
  { path: '/dashboard', name: '数据看板' },
  { path: '/monitor', name: '服务监控' },
  { path: '/security', name: '安全分析' },
  { path: '/perf', name: '性能分析' },
  { path: '/bandwidth', name: '带宽分析' },
  { path: '/activation-codes', name: '激活码管理' },
  { path: '/teachers', name: '教师管理' },
];

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const token = (() => {
    try {
      return localStorage.getItem('admin_token');
    } catch {
      return null;
    }
  })();

  const handleLogout = () => {
    void api
      .post('/admin/logout')
      .catch(() => undefined)
      .finally(() => {
        try {
          localStorage.removeItem('admin_token');
        } catch {
          // ignore
        }
        window.location.href = '/login';
      });
  };

  const avatarMenu: MenuProps = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      },
    ],
  };

  const mobileMenuItems: MenuProps['items'] = MENU_DATA.map((item) => ({ key: item.path, label: item.name }));
  const selectedKeys = [location.pathname];

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (isMobile) {
    return (
      <AntLayout style={{ minHeight: '100dvh', background: '#f0f2f5' }}>
        <AntLayout.Header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 56,
            padding: '0 12px',
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Button type="text" icon={<MenuOutlined />} onClick={() => setMobileMenuOpen(true)} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
              fontWeight: 600,
              color: '#111',
            }}
          >
            <img src={logoPet} alt="logo" style={{ width: 28, height: 28, flex: '0 0 auto' }} />
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>班级宠物系统</div>
          </div>
          <Dropdown menu={avatarMenu} placement="bottomRight" trigger={['click']}>
            <Avatar
              size={32}
              src="https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg"
              style={{ cursor: 'pointer' }}
            />
          </Dropdown>
        </AntLayout.Header>

        <AntLayout.Content style={{ padding: 12, background: '#f0f2f5' }}>
          <Outlet />
        </AntLayout.Content>

        <Drawer
          open={mobileMenuOpen}
          placement="left"
          width={260}
          onClose={() => setMobileMenuOpen(false)}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderBottom: '1px solid #f0f0f0' }}>
            <img src={logoPet} alt="logo" style={{ width: 28, height: 28 }} />
            <div style={{ fontWeight: 600 }}>后台管理</div>
          </div>
          <Menu
            mode="inline"
            items={mobileMenuItems}
            selectedKeys={selectedKeys}
            onClick={(e) => {
              navigate(e.key as string);
              setMobileMenuOpen(false);
            }}
          />
        </Drawer>
      </AntLayout>
    );
  }

  return (
    <ProLayout
      title="班级宠物系统"
      logo={logoPet}
      layout="mix"
      splitMenus={false}
      contentWidth="Fluid"
      fixedHeader
      fixSiderbar
      location={{ pathname: location.pathname }}
      menu={{
        request: async () => MENU_DATA,
      }}
      menuItemRender={(item, dom) => {
        const path = item.path;
        if (typeof path !== 'string') return dom;
        return (
          <a
            href={path}
            onClick={(e) => {
              e.preventDefault();
              navigate(path);
            }}
          >
            {dom}
          </a>
        );
      }}
      avatarProps={{
        src: 'https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg',
        title: '管理员',
        render: (_props, dom) => {
            void _props;
            return (
                <Dropdown
                    menu={avatarMenu}
                >
                    {dom}
                </Dropdown>
            );
        }
      }}
    >
      <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 56px)' }}>
        <Outlet />
      </div>
    </ProLayout>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<AnalyticsDashboard />} />
          <Route path="/monitor" element={<Monitor />} />
          <Route path="/security" element={<Security />} />
          <Route path="/perf" element={<Perf />} />
          <Route path="/bandwidth" element={<Bandwidth />} />
          <Route path="/activation-codes" element={<Dashboard />} />
          <Route path="/teachers" element={<Teachers />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
